import { type ChainflipAsset } from '@chainflip/utils/chainflip';
import { Link } from '@/shared/components/atoms/Link';
import Callout from '@/shared/components/flip-ui-kit/Callout';
import BorrowAndSupplyOverviewContent from '@/shared/components/lending/BorrowAndSupplyOverviewContent';
import PageTab from '@/shared/components/PageTab';
import { useLendingPools } from '@/shared/hooks/useLendingPools';
import { useOraclePrices } from '@/shared/hooks/useOraclePrice';
import useQueryParam from '@/shared/hooks/useQueryParam';
import ArrowIcon from '@/shared/icons/flip-ui-kit/large/Arrow';
import BorrowIcon from '@/shared/icons/flip-ui-kit/large/Borrow';
import Dots from '@/shared/icons/flip-ui-kit/large/Dots';
import MyBorrowPositionContent from 'packages/liquidity-provision/components/Lending/MyBorrowPositionContent';
import MySupplyPositionContent from 'packages/liquidity-provision/components/Lending/MySupplyPositionContent';
import Layout from '../../components/Layout';
import { LendingIntroductionModal } from '../../components/Lending/Modals/LendingIntroductionModal';

const OraclePriceStaleCallout = () => {
  const prices = useOraclePrices();
  const { lendingPools } = useLendingPools();
  const stalePrices = Object.entries(prices ?? {}).filter(
    ([cfAsset, price]) =>
      price.isStale && lendingPools.map((pool) => pool.asset).includes(cfAsset as ChainflipAsset),
  ) as [
    keyof NonNullable<ReturnType<typeof useOraclePrices>>,
    NonNullable<ReturnType<typeof useOraclePrices>>[ChainflipAsset],
  ][];

  return stalePrices?.length ? (
    <Callout type="warning">
      <div className="flex gap-x-1">
        Oracle prices are updating with a delay. Until this is resolved, borrowing or supplying the
        following assets might be unavailable:{' '}
        <div className="flex gap-x-1">
          {stalePrices.map(([cfAsset]) => (
            <Link
              color="orange"
              key={cfAsset}
              href={`/lending/markets/${cfAsset}`}
              target="_blank"
              rel="noopener noreferrer"
              className="underline transition after:content-[','] last:after:content-['']"
            >
              {cfAsset}
            </Link>
          ))}
        </div>
      </div>
    </Callout>
  ) : null;
};

export const BorrowAndSupply = () => {
  const [selectedTab, setSelectedTab] = useQueryParam({
    name: 'tab',
    defaultValue: 'overview',
    parse: (param: string) => {
      if (param === 'overview' || param === 'borrow' || param === 'supply') {
        return param;
      }
      return 'overview';
    },
  });

  let content;

  switch (selectedTab) {
    case 'overview':
      content = (
        <BorrowAndSupplyOverviewContent
          onSupplyClick={() => setSelectedTab('supply')}
          onBorrowClick={() => setSelectedTab('borrow')}
        />
      );
      break;
    case 'borrow':
      content = <MyBorrowPositionContent />;
      break;
    case 'supply':
      content = <MySupplyPositionContent onBorrowClick={() => setSelectedTab('borrow')} />;
      break;
    default:
      content = null;
  }

  return (
    <Layout title="Lending">
      <>
        <div className="flex flex-col gap-y-6">
          <OraclePriceStaleCallout />
          <PageTab
            selectedTab={selectedTab}
            onSelect={setSelectedTab}
            options={[
              {
                Icon: Dots,
                iconClassName: 'group-data-active:text-cf-green-1',
                title: 'Overview',
                value: 'overview',
              },
              {
                Icon: BorrowIcon,
                iconClassName: 'group-data-active:text-cf-green-1',
                title: 'Borrow',
                value: 'borrow',
              },
              {
                Icon: ArrowIcon,
                iconDirection: 'up',
                iconClassName: 'group-data-active:text-cf-green-1',
                title: 'Supply',
                value: 'supply',
              },
            ]}
          />
        </div>
        <div className="mt-6">{content}</div>
        <LendingIntroductionModal />
      </>
    </Layout>
  );
};

export default BorrowAndSupply;
